import { Link } from '@tanstack/react-router';
import type { AgentWorkflowRole, AgentWorkflowUsage } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { RobotIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';

/** Matches `AGENT_WORKFLOW_ROLE_ORDER` on the server. */
const ROLE_ORDER: readonly AgentWorkflowRole[] = [
  'setup',
  'teardown',
  'action',
];

/** The workflow row navigates on click; badges and links must not. */
function stopRowNavigation(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

interface AgentWorkflowUsageBadgesProps {
  usages: AgentWorkflowUsage[];
  className?: string;
}

/**
 * "Agents run this workflow" — one badge per role (setup, teardown, action),
 * with the agents behind each role in a popover. Renders nothing when no
 * published agent uses the workflow.
 */
export function AgentWorkflowUsageBadges({
  usages,
  className,
}: AgentWorkflowUsageBadgesProps) {
  const gt = useGT();
  const groups = ROLE_ORDER.flatMap((role) => {
    const agents = usages.filter((usage) => usage.roles.includes(role));
    return agents.length > 0 ? [{ role, agents }] : [];
  });
  if (groups.length === 0) return null;

  const roleLabel = roleLabels(gt);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 align-middle',
            className,
          )}
          aria-label={gt('Used by agents: {roles}', {
            context: 'button opening agent setup, teardown, and action usage',
            roles: groups
              .map((group) => `${roleLabel[group.role]} ${group.agents.length}`)
              .join(', '),
          })}
          onClick={stopRowNavigation}
        >
          <RobotIcon aria-hidden className="size-3.5 text-muted-foreground" />
          {groups.map((group) => (
            <Badge key={group.role} variant="outline" className="text-xs">
              {roleLabel[group.role]} {group.agents.length}
            </Badge>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-72 p-3"
        onClick={stopRowNavigation}
      >
        <p className="text-sm font-medium">
          <T context="popover title for agents that use a workflow">
            Used by agents
          </T>
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <T context="popover note that only published agent configuration counts">
            From each agent's published configuration.
          </T>
        </p>
        <div className="mt-2 space-y-2">
          {groups.map((group) => (
            <div key={group.role} className="space-y-0.5">
              <p className="text-xs font-medium text-muted-foreground">
                {roleLabel[group.role]}
              </p>
              {group.agents.map((agent) => (
                <AgentUsageLink
                  key={agent.agentId}
                  role={group.role}
                  agent={agent}
                />
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function roleLabels(
  gt: ReturnType<typeof useGT>,
): Record<AgentWorkflowRole, string> {
  return {
    setup: gt('Setup', {
      context: 'badge: workflow used as an agent setup phase',
    }),
    teardown: gt('Teardown', {
      context: 'badge: workflow used as an agent teardown phase',
    }),
    action: gt('Action', {
      context: 'badge: workflow exposed as an agent action tool',
    }),
  };
}

function AgentUsageLink({
  role,
  agent,
}: {
  role: AgentWorkflowRole;
  agent: AgentWorkflowUsage;
}) {
  const label = <AgentUsageLabel agent={agent} />;
  const className =
    'flex items-center justify-between gap-2 rounded-sm px-1.5 py-1 text-sm hover:bg-accent';
  if (role === 'action') {
    return (
      <Link
        to="/agents/$agentDefinitionId/tools"
        params={{ agentDefinitionId: agent.agentId }}
        className={className}
        onClick={stopRowNavigation}
      >
        {label}
      </Link>
    );
  }
  return (
    <Link
      to="/agents/$agentDefinitionId/task-management"
      params={{ agentDefinitionId: agent.agentId }}
      className={className}
      onClick={stopRowNavigation}
    >
      {label}
    </Link>
  );
}

function AgentUsageLabel({ agent }: { agent: AgentWorkflowUsage }) {
  return (
    <>
      <span className="truncate">{agent.agentName}</span>
      {!agent.isActive && (
        <span className="shrink-0 text-xs text-muted-foreground">
          <T context="label on a paused agent in the workflow usage popover">
            Paused
          </T>
        </span>
      )}
    </>
  );
}
